/**
 * GenerationHistoryDropdown Component
 * Shows recent pick generations grouped by time for quick access.
 * Supports individual deletion and responsive scroll on all viewports.
 */

import type { GenerationHistory } from '@stores/generationStore';
import { useGenerationStore } from '@stores/generationStore';
import { ChevronDown, Clock, Trash2, X } from 'lucide-react';
import type { ReactElement } from 'react';
import { useLayoutEffect, useMemo, useRef, useState } from 'react';

interface GenerationHistoryDropdownProps {
  onLoadEntry?: () => void;
  /** 'up' opens above the button (default), 'down' opens below */
  direction?: 'up' | 'down';
}

/** The open panel starts at least this far from the screen's left edge; its max width (100vw - 2rem) leaves room for that. */
const PANEL_VIEWPORT_MARGIN_PX = 16;

// =============================================================================
// Helpers
// =============================================================================

/** Format relative time for display */
function formatRelativeTime(timestamp: number): string {
  const now = Date.now();
  const diff = now - timestamp;
  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);

  if (days > 0) return `${days}d ago`;
  if (hours > 0) return `${hours}h ago`;
  if (minutes > 0) return `${minutes}m ago`;
  return 'Just now';
}

/** Group label for a timestamp */
function getTimeGroup(timestamp: number): string {
  const now = new Date();
  const date = new Date(timestamp);

  // Same calendar day
  if (
    date.getFullYear() === now.getFullYear() &&
    date.getMonth() === now.getMonth() &&
    date.getDate() === now.getDate()
  ) {
    const hours = now.getHours() - date.getHours();
    if (hours < 1) return 'Just now';
    return 'Earlier today';
  }

  // Yesterday
  const yesterday = new Date(now);
  yesterday.setDate(yesterday.getDate() - 1);
  if (
    date.getFullYear() === yesterday.getFullYear() &&
    date.getMonth() === yesterday.getMonth() &&
    date.getDate() === yesterday.getDate()
  ) {
    return 'Yesterday';
  }

  return 'Older';
}

interface GroupedEntries {
  label: string;
  entries: GenerationHistory[];
}

/** Group entries by time period, preserving order */
function groupByTime(entries: GenerationHistory[]): GroupedEntries[] {
  const groups: GroupedEntries[] = [];
  let currentLabel = '';

  for (const entry of entries) {
    const label = getTimeGroup(entry.timestamp);
    if (label !== currentLabel) {
      groups.push({ label, entries: [entry] });
      currentLabel = label;
    } else {
      groups[groups.length - 1].entries.push(entry);
    }
  }

  return groups;
}

// =============================================================================
// Entry Component
// =============================================================================

interface HistoryEntryProps {
  entry: GenerationHistory;
  onLoad: () => void;
  onRemove: () => void;
}

function HistoryEntry({ entry, onLoad, onRemove }: HistoryEntryProps): ReactElement {
  const gameCount = entry.gameIds.length;
  // Defensive: a failed entry carries no picks, and older cached entries predate the field.
  const pickCount = entry.picks?.picks?.length ?? 0;
  const mode = entry.config.outputOptions?.analysisMode || 'standard';
  const isFailed = entry.failed === true;

  return (
    <div
      className={`group flex items-center transition ${isFailed ? 'opacity-60' : 'hover:bg-theme-surface-hover'}`}
    >
      <button
        type="button"
        onClick={onLoad}
        disabled={isFailed}
        aria-disabled={isFailed}
        title={isFailed ? (entry.failureReason ?? 'This generation produced no picks') : undefined}
        className={`flex-1 text-left p-3 min-w-0 ${isFailed ? 'cursor-not-allowed' : ''}`}
      >
        {/* Timestamp + status badge */}
        <div className="flex items-center justify-between mb-1">
          <span className="text-xs text-theme-text-tertiary">
            {formatRelativeTime(entry.timestamp)}
          </span>
          {isFailed && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-theme-danger/20 text-theme-danger">
              FAILED
            </span>
          )}
          {!isFailed && entry.picks?.cancelled && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-theme-text-tertiary/20 text-theme-text-tertiary">
              CANCELLED
            </span>
          )}
          {!isFailed && entry.picks?.cached && (
            <span className="text-[10px] px-1.5 py-0.5 rounded bg-theme-warning/20 text-theme-warning">
              CACHED
            </span>
          )}
        </div>

        {/* Summary: the failure reason replaces the pick count, which would read "0 picks" */}
        {isFailed ? (
          <div className="text-sm text-theme-text-secondary font-medium mb-1">
            {entry.failureReason ?? 'No picks produced'}
          </div>
        ) : (
          <div className="text-sm text-theme-text font-medium mb-1">
            {pickCount} {pickCount === 1 ? 'pick' : 'picks'} · {mode}
          </div>
        )}

        {/* Details */}
        <div className="text-xs text-theme-text-secondary">
          {gameCount} {gameCount === 1 ? 'game' : 'games'}
          {isFailed ? ` · ${mode}` : ''}
        </div>
      </button>

      {/* Individual delete */}
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          onRemove();
        }}
        className="p-2 mr-1 rounded opacity-0 group-hover:opacity-100 hover:bg-theme-danger/20 text-theme-text-tertiary hover:text-theme-danger transition shrink-0"
        title="Remove this generation"
      >
        <X className="w-3.5 h-3.5" />
      </button>
    </div>
  );
}

// =============================================================================
// Dropdown Component
// =============================================================================

export function GenerationHistoryDropdown({
  onLoadEntry,
  direction = 'up',
}: GenerationHistoryDropdownProps): ReactElement | null {
  const { generationHistory, loadFromHistory, removeFromHistory, clearHistory } =
    useGenerationStore();
  const [isOpen, setIsOpen] = useState(false);
  const anchorRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const [panelShiftPx, setPanelShiftPx] = useState(0);

  const groups = useMemo(() => groupByTime(generationHistory), [generationHistory]);

  // The panel hangs left from the trigger's right edge, which on a phone can sit closer to the
  // left edge than the panel is wide, so shift it back on screen.
  useLayoutEffect(() => {
    if (!isOpen) return;
    const keepPanelOnScreen = (): void => {
      const anchor = anchorRef.current;
      const panel = panelRef.current;
      if (!anchor || !panel) return;
      const panelLeft = anchor.getBoundingClientRect().right - panel.offsetWidth;
      setPanelShiftPx(Math.max(0, PANEL_VIEWPORT_MARGIN_PX - panelLeft));
    };
    keepPanelOnScreen();
    window.addEventListener('resize', keepPanelOnScreen);
    return () => window.removeEventListener('resize', keepPanelOnScreen);
  }, [isOpen]);

  if (generationHistory.length === 0) {
    return null;
  }

  return (
    <div ref={anchorRef} className="relative">
      <button
        type="button"
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-1 sm:gap-1.5 px-2 sm:px-3 py-1.5 rounded-full bg-theme-primary/10 border border-theme-primary/20 text-theme-primary text-sm font-medium transition hover:bg-theme-primary/20 hover:border-theme-primary/40"
        title="View recent generations"
      >
        <Clock className="w-3.5 h-3.5" />
        <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1 rounded-full bg-theme-primary/20 text-xs font-bold">
          {generationHistory.length}
        </span>
        <span className="hidden @2xl:inline">recent</span>
        <ChevronDown className={`w-3.5 h-3.5 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
      </button>

      {isOpen && (
        <>
          {/* Backdrop */}
          <div className="fixed inset-0 z-40" onClick={() => setIsOpen(false)} />

          {/* Dropdown — responsive max-height: ~60% viewport on mobile, fixed on desktop */}
          <div
            ref={panelRef}
            data-testid="generation-history-panel"
            style={panelShiftPx > 0 ? { right: -panelShiftPx } : undefined}
            className={`absolute right-0 w-80 max-w-[calc(100vw-2rem)] bg-theme-surface border border-theme-border rounded-lg shadow-lg z-50 max-h-[min(24rem,60dvh)] flex flex-col ${direction === 'down' ? 'top-full mt-2' : 'bottom-full mb-2'}`}
          >
            {/* Header (sticky) */}
            <div className="flex items-center justify-between p-3 border-b border-theme-border shrink-0">
              <span className="text-sm font-medium text-theme-text">Recent Generations</span>
              {generationHistory.length > 1 && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    if (confirm('Clear all generation history?')) {
                      clearHistory();
                      setIsOpen(false);
                    }
                  }}
                  className="p-1 rounded hover:bg-theme-surface-hover text-theme-text-tertiary hover:text-theme-danger transition"
                  title="Clear all history"
                >
                  <Trash2 className="w-4 h-4" />
                </button>
              )}
            </div>

            {/* Scrollable entries */}
            <div className="overflow-y-auto overscroll-contain">
              {groups.map((group) => (
                <div key={group.label}>
                  {/* Group header */}
                  <div className="sticky top-0 px-3 py-1.5 bg-theme-bg/90 backdrop-blur-sm border-b border-theme-border/50">
                    <span className="text-[10px] uppercase tracking-wider text-theme-text-tertiary font-semibold">
                      {group.label}
                    </span>
                  </div>

                  {/* Group entries */}
                  <div className="divide-y divide-theme-border/50">
                    {group.entries.map((entry) => (
                      <HistoryEntry
                        key={entry.id}
                        entry={entry}
                        onLoad={() => {
                          loadFromHistory(entry.id);
                          onLoadEntry?.();
                          setIsOpen(false);
                        }}
                        onRemove={() => removeFromHistory(entry.id)}
                      />
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
